import { Menu, LogOut, User, Settings, Bell, Search, Sparkles } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
} from '@alertwhale/ui'
import { useAppStore } from '@/stores/appStore'
import { useAuthStore } from '@/stores/authStore'
import { useLogout } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { getInitials } from '@/lib/utils'

export function Header() {
  const { toggleSidebar } = useAppStore()
  const { user } = useAuthStore()
  const logout = useLogout()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout.mutateAsync()
    navigate('/login')
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl px-6">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden text-muted-foreground hover:text-foreground hover:bg-white/[0.05]"
        onClick={toggleSidebar}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Search bar */}
      <div className="hidden md:flex flex-1 max-w-md">
        <div className="relative w-full group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 transition-colors group-focus-within:text-whale-400" />
          <Input
            type="search"
            placeholder="Search applications, notifications..."
            className="w-full pl-10 h-9 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:bg-slate-900 focus:border-whale-500/50 transition-all"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-800 rounded border border-slate-700">
              /
            </kbd>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1 md:hidden" />

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Status indicator */}
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 border border-success/20">
          <div className="w-1.5 h-1.5 rounded-full bg-success pulse-live" />
          <span className="text-xs font-medium text-success">All Systems Operational</span>
        </div>

        {/* Notifications */}
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-foreground hover:bg-white/[0.05]"
        >
          <Bell className="h-5 w-5" />
          {/* Notification badge */}
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
        </Button>

        {/* Upgrade button (for free users) */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden lg:flex items-center gap-2 text-whale-400 hover:text-whale-300 hover:bg-whale-500/10 border border-whale-500/20"
          onClick={() => navigate('/billing')}
        >
          <Sparkles className="h-4 w-4" />
          <span>Upgrade</span>
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="relative h-9 w-9 rounded-full ring-2 ring-slate-700 hover:ring-whale-500/50 transition-all"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatarUrl || undefined} alt={user?.name || ''} />
                <AvatarFallback className="bg-gradient-to-br from-whale-500/20 to-whale-600/10 text-whale-400 font-semibold">
                  {user?.name ? getInitials(user.name) : user?.email?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 p-2 bg-slate-900 border-slate-700 shadow-2xl"
            align="end"
            forceMount
          >
            <DropdownMenuLabel className="font-normal p-3 rounded-lg bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.avatarUrl || undefined} alt={user?.name || ''} />
                  <AvatarFallback className="bg-gradient-to-br from-whale-500/20 to-whale-600/10 text-whale-400 font-semibold">
                    {user?.name ? getInitials(user.name) : user?.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col space-y-0.5">
                  <p className="text-sm font-semibold leading-none">{user?.name || 'User'}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-2 bg-white/[0.06]" />
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.05]">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Profile</span>
                <span className="text-xs text-muted-foreground">Manage your account</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.05]">
                <Settings className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Settings</span>
                <span className="text-xs text-muted-foreground">Preferences & config</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-2 bg-white/[0.06]" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-destructive hover:bg-destructive/10 focus:bg-destructive/10"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10">
                <LogOut className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium">Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
